import React, { useEffect, useMemo, useState } from "react";
import { LinearProgress, Box } from "@mui/material";
import { useQuery } from "@tanstack/react-query";
import { useAppState } from "Context/StateContext";
import { Company } from "Models/company";
import { useTranslation } from "react-i18next";

import UserCompanyService from "Services/user.company.service";
import { Status } from "Util/enums";
import { CompanyNameAndIconContainer } from "../CompanyNameAndIconContainer";
import SearchFilterCompany from "../SearchFilterCompany";
import { FilterOption, UserBlockCompanyMenuProps } from "../types";
import SvgIconUrl from "../../../helpers/SvgIcon/SvgIconUrl";
import { getBrandCustom } from "Util/getCustomColor";

import {
  CompanyMenuListContainer,
  Divider,
  GridItemContainer,
  SwitchCompanyOption,
  UserProfileSectionTitle,
} from "./styles";

const BACKICON = require("Assets/icons/arrow-back-2.svg");

export const UserBlockCompanyMenu = ({
  handleSwitchCompany,
  handleCloseCompanyMenu,
  loadingSwitchCompany,
}: UserBlockCompanyMenuProps) => {
  const { t: translate } = useTranslation();
  const { currentUser } = useAppState();
  const [searchInput, setSearchInput] = useState<string>("");
  const [filterOptions, setFilterOptions] = useState<FilterOption[]>([]);

  const { data: companies, isFetching } = useQuery({
    queryKey: ["user.company.list", currentUser?.id, currentUser?.companyId],
    queryFn: () =>
      UserCompanyService.getAll({
        userId: currentUser?.id,
        status: Status.ACTIVE,
        include: JSON.stringify([
          {
            association: "company",
            attributes: ["id", "name"],
            include: [
              { association: "icon" },
              { association: "profile", attributes: ["id", "name"] },
            ],
            where: { status: Status.ACTIVE },
          },
        ]),
        sortBy: JSON.stringify([["company", "name", "asc"]]),
      }).then(({ data }) => data.map((uc) => uc.company)),
    initialData: [] as Company[],
    refetchOnWindowFocus: false,
    enabled: !!currentUser?.id,
  });

  useEffect(() => {
    const profileCompanies = new Map<number | undefined, string>();
    companies.forEach((c) => profileCompanies.set(c.profile?.id, c.profile?.name));
    setFilterOptions(
      Array.from(profileCompanies).map(([key, value]) => ({
        key,
        value,
        checked: false,
      }))
    );
  }, [companies]);

  const filteredCompanies = useMemo(() => {
    const filterSelectedKeys = filterOptions.filter((f) => f.checked).map((f) => f.key);
    const valuesWithFilter = filterSelectedKeys?.length
      ? companies.filter((c) => filterSelectedKeys.includes(c.profile?.id))
      : companies;

    return valuesWithFilter.filter(
      (c) =>
        c.id !== currentUser?.companyId &&
        (searchInput.trim() ? c.name?.toLowerCase()?.includes(searchInput.toLowerCase()) : true)
    );
  }, [searchInput, filterOptions, companies, currentUser?.companyId]);

  return (
    <GridItemContainer container direction="column">
      <Box>
        <Box sx={{ display: "flex", mb: "12px", flexDirection: "column" }}>
          <Box
            sx={{
              color: getBrandCustom("primaryColor", "#0055A4"),
              cursor: "pointer",
              display: "flex",
            }}
            onClick={handleCloseCompanyMenu}
          >
            <SvgIconUrl src={BACKICON} size={20} />
          </Box>
        </Box>
        <UserProfileSectionTitle>{translate("currentlyViewing")}</UserProfileSectionTitle>
        {currentUser && (
          <CompanyNameAndIconContainer company={currentUser.company} showProfileName />
        )}

        <Divider />

        <UserProfileSectionTitle>{translate("changeTo")}</UserProfileSectionTitle>

        <SearchFilterCompany
          onChangeInput={setSearchInput}
          searchInput={searchInput}
          setFilterOptions={setFilterOptions}
          filterOptions={filterOptions}
        />

        {isFetching && <LinearProgress sx={{ height: "4px", borderRadius: "8px", mt: 1 }} />}
      </Box>

      <CompanyMenuListContainer scroll={filteredCompanies.length > 5}>
        {filteredCompanies.map((company, idx) => (
          <SwitchCompanyOption
            key={company.id}
            disabled={loadingSwitchCompany}
            data-cy={`companySwitchOption${idx}`}
            onClick={() => handleSwitchCompany(company)}
          >
            <CompanyNameAndIconContainer company={company} showProfileName />
          </SwitchCompanyOption>
        ))}
      </CompanyMenuListContainer>
    </GridItemContainer>
  );
};
